import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";
import classNames from "classnames";

import Board from "./components/Board";
import { MultiSelectPcPropsType } from "./type";
import { DropdownIconBluePc, DropdownIconDark, DropdownIconPc, PlaceIcon } from "./assets";
import { isCustomOption } from "./utils/custom";

import styles from "./style/index.module.scss";

const checkedProps = {
  "data-selected": true
};

const findOpt = (options = [], value = []) => {
  let results = [];
  let list = options;
  (value || []).find((val) => {
    let target = list.find((v) => v.value === val);
    //
    if (target) {
      results.push(target);
      list = target.children;
      //
      return false;
    }
    //
    results = [];
    return true;
  });
  //
  return results;
};

export const SingleCascaderSelectForCustom = ({
  options,
  value,
  onChange,
  searchBoardPlaceholder,
  visibleSearch,
  oneLabel,
  placeholder = "",
  wrapperClassName,
  arrowIconStyle = "dark",
  cascaderPanelProps,
  cascaderStyles,
  onlySearchInLastedLayer,
  renderSearchEmpty,
  suffixIcon,
  ...rest
}: MultiSelectPcPropsType) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectValue, setSelectValue] = useState(value);
  const prevSelectOpt = useRef([]);
  const customOpt = useRef(null);

  const memoOptions = useMemo(() => {
    if (isCustomOption(value[0])) {
      return [customOpt.current || {}];
    }
    return findOpt(options, value);
  }, [options, value]);
  useMemo(() => {
    let newValues = prevSelectOpt.current.map((v) => {
      return v.value;
    });

    if (newValues.length) {
      if (findOpt(options, newValues).length) {
        findOpt(options, newValues).forEach((item) => {
          if (item.originalLabel) {
            item.label = item.originalLabel;
          }
        });
      }
    }

    prevSelectOpt.current = memoOptions;
  }, [memoOptions, options]);

  useMemo(() => {
    memoOptions.forEach((v) => {
      v.originalLabel = v.label;
      v.label = <span {...checkedProps}>{v.label}</span>;
    });
  }, [memoOptions]);

  useEffect(() => {
    setSelectValue(value);
  }, [value, open]);

  const onMultipleChange = (value, options) => {
    setSelectValue(value);
    if (isCustomOption(value[0])) {
      customOpt.current = options[0];
    } else {
      customOpt.current = null;
    }
    onChange(value, options);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);

  const onCloseDropdown = () => {
    setOpen(false);
  };

  const customLabel = (
    <div className={styles.inputLabel}>
      <div className={!value?.length ? "selectLabelPlaceholder" : "selectLabel"}>
        {memoOptions[memoOptions.length - 1]?.label || placeholder || ""}
      </div>
    </div>
  );

  const DropdownIcon = (): ReactNode => {
    if (arrowIconStyle == "dark") {
      return <DropdownIconDark />;
    }

    return !!value?.length ? <DropdownIconBluePc /> : <DropdownIconPc />;
  };

  return (
    <div
      className={classNames(
        {
          [styles.wrapperSelect]: true,
          [styles.wrapperSelectActive]: open
        },
        wrapperClassName
      )}
    >
      <Select
        className={classNames(
          {
            controlAntdSelect: true,
            [styles.overrideSelect]: true,
            [styles.openState]: true,
            activeLabel: !!value?.length
          },
          "initGlobalSelect"
        )}
        open={open}
        onDropdownVisibleChange={(visible) => setOpen(visible)}
        dropdownRender={(menu) => (
          <Board
            cascaderStyles={cascaderStyles}
            options={options}
            value={value}
            onCloseDropdown={onCloseDropdown}
            searchBoardPlaceholder={searchBoardPlaceholder}
            visibleSearch={visibleSearch}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setSelectValue={setSelectValue}
            selectValue={selectValue}
            onMultipleChange={onMultipleChange}
            renderSearchEmpty={renderSearchEmpty}
            onlySearchInLastedLayer={onlySearchInLastedLayer}
            cascaderPanelProps={cascaderPanelProps}
          />
        )}
        getPopupContainer={(node) => node.parentElement}
        placement='bottomLeft'
        suffixIcon={suffixIcon || DropdownIcon()}
        options={[]}
        {...rest}
        optionLabelProp='label'
        value={{
          value: "customValue",
          label: customLabel
        }}
      />
    </div>
  );
};
